import React, { useState } from 'react'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import BasicButton, { BASIC_BUTTON_SIZE_SMALL, BASIC_BUTTON_TYPE_OUTLINED } from '@divvy-web/skylab.basicbutton'
import Icon from '@divvy-web/skylab.icon'

import ImageWrapper from '@sputnik/components/ImageWrapper'
import { Collaborator, InviteCollaboratorsDrawer } from '@sputnik/components/InviteCollaborators'
import useApplicationCollaborators, { useTrackCollaboratorEvent } from '@sputnik/hooks/useApplicationCollaborators'

import styles from './styles.module.scss'

export interface InviteCollaboratorsSectionProps {
  salesforceCreditId: string
}

const InviteCollaboratorsSection = ({ salesforceCreditId }: InviteCollaboratorsSectionProps) => {
  const [getClassName, makeTestId] = useNameFormatter('InviteCollaboratorsSection')
  const [isShowingInviteCollaboratorsDrawer, setIsShowingInviteCollaboratorsDrawer] = useState(false)

  const { allCollaborators } = useApplicationCollaborators({ salesforceCreditId })

  const { trackCollaboratorEvent } = useTrackCollaboratorEvent({ salesforceCreditId })

  const hasCollaborators = allCollaborators.length > 0

  return (
    <div
      className={styles[getClassName('')]}
      data-testid={makeTestId('')}
    >
      <div className={styles[getClassName('header')]}>
        <div className={styles[getClassName('header-title')]}>
          <FormattedMessage
            defaultMessage='Collaborators'
            id='sputnik.InviteCollaboratorsSection__9Jstnt'
          />
        </div>
        <BasicButton
          className={styles[getClassName('invite-button')]}
          size={BASIC_BUTTON_SIZE_SMALL}
          type={BASIC_BUTTON_TYPE_OUTLINED}
          onClick={() => {
            trackCollaboratorEvent(
              'se_signup__application_dashboard_application_details_drawer_collaborator__fired',
              (role) => ({
                person_type: role,
              }),
            )
            setIsShowingInviteCollaboratorsDrawer(true)
          }}
        >
          <Icon name={hasCollaborators ? 'edit' : 'plus'} />
          <FormattedMessage
            defaultMessage='{hasCollaborators, select, true {Manage} other {Invite}}'
            id='sputnik.InviteCollaboratorsSection__0Azlrb'
            values={{
              hasCollaborators,
            }}
          />
        </BasicButton>
      </div>
      {!hasCollaborators ? (
        <div className={styles[getClassName('invite-prompt')]}>
          <ImageWrapper
            className={styles[getClassName('invite-prompt-image')]}
            imageName='invite-credit-application-collaborators'
          />
          <div className={styles[getClassName('invite-prompt-text')]}>
            <FormattedMessage
              defaultMessage='Need help filling out the application?'
              id='sputnik.InviteCollaboratorsSection__RDu7c4'
            />
            <FormattedMessage
              defaultMessage='Invite a collaborator to help you!'
              id='sputnik.InviteCollaboratorsSection__Heg6Ei'
            />
          </div>
        </div>
      ) : (
        <div className={styles[getClassName('collaborators')]}>
          {allCollaborators.map((collaborator) => (
            <Collaborator
              key={collaborator.email}
              {...collaborator}
              isRemoveDisabled
              appId={salesforceCreditId}
            />
          ))}
        </div>
      )}
      <InviteCollaboratorsDrawer
        appId={salesforceCreditId}
        icon='back'
        isShowing={isShowingInviteCollaboratorsDrawer}
        onClose={() => {
          trackCollaboratorEvent('se_signup__application_dashboard_invite_collaborator_cancel__fired')
          setIsShowingInviteCollaboratorsDrawer(false)
        }}
        onDeleteCollaborator={() => {
          trackCollaboratorEvent(
            'se_signup__application_dashboard_invite_collaborator_delete_collaborator__submitted',
            (role) => ({
              person_type: role,
            }),
          )
        }}
        onInviteCollaborator={() => {
          trackCollaboratorEvent('se_signup__application_dashboard_invite_collaborator__submitted', (role) => ({
            person_type: role,
          }))
        }}
      />
    </div>
  )
}

export default InviteCollaboratorsSection
